import React, {useEffect, useState} from 'react';
import OeTip from './OeTip';
import DocPanel from './DocPanel';
import DeliverResult from './DeliverResult';
import VeVorschau from './VeVorschau';
import {finalizeShipment, loadDocs, loadSnvInfo, saveImoPallets, setSnvVersand, shortfallNote} from './api';
import {fmt0} from './format';
import {
    SNV_PALETTE_AB_STK,
    SNV_STK_JE_PAKET,
    WH_LAHR,
    docForShipment,
    hasSchneiderLS,
    snvShipment,
    snvVersandInfo,
    sourceNormalization,
    splitPlan,
    uncoveredPrimaryRows,
} from './wizard';

// Schritt 3 des geführten Ablaufs: der Lieferschein ab Schneider (SNV).
// Original `renderWarehouse`, Zweig `WIZ_STEP === 3` (4105–4300), plus die
// Modale `openSnvSendModal`/`openSnvCancelModal` (4801–4826).
// ABGLEICH G10–G15, F11, F12, J53, K29, K30.
//
// Zwei Fälle: Ist noch kein Beleg da, wird die Versandart gewählt (Pflicht)
// und der Lieferschein erzeugt. Existiert er, steht hier der Beleg selbst —
// ansehen, drucken, versenden oder stornieren.

/** Warnung über Lahr-Positionen ohne Deckung (Original `ucWarn`, 4113). */
function UncoveredWarn({anzahl, onStep}) {
    if (!anzahl) {
        return null;
    }
    return (
        <div className="srcnote" style={{borderColor: 'var(--warn)'}}>
            <span>
                ⚠ <b>{anzahl} Lahr-Position(en) ohne Deckung</b> — bitte in Schritt{' '}
                <OeTip tip={'Springt zum Schritt „Kommissionierung", dort lassen sich die Positionen per Entnahme für diesen Auftrag reservieren.'}>
                    <button type="button" className="link" onClick={() => onStep(6)}>„Kommissionierung" (6)</button>
                </OeTip>
                {' '}per Entnahme reservieren, sonst bleibt der Lahr-Rest offen.
            </span>
        </div>
    );
}

/** „Wohin geht die Schneider-Ware?" (Original `srcNote`, 4136–4160). */
function SourceNote({norm, laName, snName, orderSource, onSource}) {
    if (!norm.bothSides) {
        const only = norm.lahrN > 0 ? laName : snName;
        return (
            <div className="srcnote">
                <span>🏭 Alles läuft ab <b>{only}</b> — der Kunde bekommt <b>1 Sendung</b>.</span>
            </div>
        );
    }

    const isDirect = orderSource === 'split';
    const beistellTxt = norm.snvSmaller
        ? <>{snName} schickt seine {norm.snvN} Position(en) nach {laName} — <b>{laName} schickt alles zum Kunden</b></>
        : <>{laName} schickt seine {norm.lahrN} Position(en) nach {snName} — <b>{snName} schickt alles zum Kunden</b></>;

    return (
        <div className="srcnote">
            <div className="srcq">
                🏭 <b>Wohin geht die Schneider-Ware?</b>
                <span className="muted">
                    Nur für den Kommentar am Auftrag — an den Lieferscheinen ändert das nichts, es entstehen
                    ohnehin zwei (einer je Lager).
                </span>
            </div>
            <div className="srctoggle">
                <OeTip tip="Jede Seite schickt ihre Ware direkt zum Kunden. Der Kunde bekommt zwei Sendungen. Die Wahl steht nur im Auftragskommentar.">
                    <button
                        type="button"
                        className={`srcopt${isDirect ? ' on' : ''}`}
                        aria-pressed={isDirect}
                        onClick={() => onSource('split')}
                    >
                        Direkt zum Kunden
                        <span>Jede Seite schickt direkt · <b>2 Sendungen</b> beim Kunden</span>
                    </button>
                </OeTip>
                <OeTip tip="Die kleinere Seite stellt der größeren bei; der Kunde bekommt eine Sendung. Die Wahl steht nur im Auftragskommentar.">
                    <button
                        type="button"
                        className={`srcopt${isDirect ? '' : ' on'}`}
                        aria-pressed={!isDirect}
                        onClick={() => onSource(norm.beistellSrc)}
                    >
                        Erst nach Lahr
                        <span>{beistellTxt} · <b>1 Sendung</b> beim Kunden</span>
                    </button>
                </OeTip>
            </div>
        </div>
    );
}

export default function StepSchneiderLS({
    ctx,
    wizard,
    deliver,
    orderRef,
    dispatch,
    toast,
    ask,
    abilities,
    onStep,
    onDeliver,
    onReload,
}) {
    const {order, rows, stock} = ctx;
    const plan = splitPlan(WH_LAHR, rows, stock);
    const snvShip = snvShipment(order);
    const [imo, setImo] = useState('');
    const [imoStatus, setImoStatus] = useState('');
    const [imoBusy, setImoBusy] = useState(false);
    const [snvStatus, setSnvStatus] = useState(null);
    const [snvBusy, setSnvBusy] = useState(false);

    const shipId = snvShip ? String(snvShip.id) : null;
    const docsGeladen = wizard.shipDocs.id === shipId;
    const infoGeladen = wizard.snvInfo.id === shipId;

    // Beleg-PDF und Schneider-Status erst holen, wenn der Schritt sichtbar ist
    // (Original `loadShipDocs`/`loadSnvInfo`, 3178/3192).
    useEffect(() => {
        if (!shipId || docsGeladen || wizard.shipDocs.loading) {
            return;
        }
        dispatch({type: 'wiz-patch', patch: {shipDocs: {id: shipId, docs: null, loading: true}}});
        loadDocs(orderRef, {type: 'shipment', id: shipId})
            .then((d) => dispatch({type: 'wiz-patch', patch: {shipDocs: {id: shipId, docs: d.docs || [], loading: false}}}))
            .catch(() => dispatch({type: 'wiz-patch', patch: {shipDocs: {id: shipId, docs: [], loading: false}}}));
    }, [shipId, docsGeladen, wizard.shipDocs.loading, orderRef, dispatch]);

    useEffect(() => {
        if (!shipId || infoGeladen || wizard.snvInfo.loading) {
            return;
        }
        dispatch({type: 'wiz-patch', patch: {snvInfo: {id: shipId, data: null, loading: true}}});
        loadSnvInfo(orderRef, shipId)
            .then((j) => dispatch({type: 'wiz-patch', patch: {snvInfo: {id: shipId, data: j && j.ok ? j : null, loading: false}}}))
            .catch(() => dispatch({type: 'wiz-patch', patch: {snvInfo: {id: shipId, data: null, loading: false}}}));
    }, [shipId, infoGeladen, wizard.snvInfo.loading, orderRef, dispatch]);

    const info = (infoGeladen && wizard.snvInfo.data) ? wizard.snvInfo.data : null;

    // Gespeicherte Palettenzahl in das Eingabefeld übernehmen, sobald sie da ist.
    useEffect(() => {
        if (info && info.imoPallets != null) {
            setImo(String(info.imoPallets));
        }
    }, [info && info.imoPallets]);

    if (!plan) {
        return null;
    }

    const norm = sourceNormalization(wizard.orderSource, plan);
    const laName = ((stock.warehouses || []).find((w) => String(w.id) === WH_LAHR) || {}).name || 'Lahr';
    const snName = ((stock.warehouses || []).find((w) => String(w.id) === '8034') || {}).name || 'Schneider (SNV)';
    const uc = uncoveredPrimaryRows(plan, rows, stock);
    const openShip = !!order.openShipments;
    const canShip = !!(abilities && abilities.ship);

    // ── Fall „kein Schneider-Anteil" ────────────────────────────────────────
    if (!hasSchneiderLS(ctx) && !snvShip) {
        return (
            <div className="srcnote">
                <span>
                    🏭 <b>Kein Schneider-Anteil.</b> Es läuft nichts über Schneider (SNV) — hier ist nichts zu tun.
                    Weiter zum Komischein.
                </span>
            </div>
        );
    }

    // ── Fall „Lieferschein liegt vor" ───────────────────────────────────────
    if (snvShip) {
        const doc = docForShipment(snvShip, wizard.shipDocs);
        const shipped = !snvShip.open;
        const versandart = info ? info.versandart : null;
        const vLbl = versandart === 'palette' ? 'Palette' : (versandart === 'paket' ? 'Paket' : null);

        const versenden = () => ask({
            title: 'Schneider-Lieferschein versenden',
            confirmLabel: 'Ja, versenden',
            tone: 'danger',
            body: (
                <>
                    <p className="chg">
                        🚚 Lieferschein <b>{snvShip.number}</b> wird versendet{vLbl ? ` (${vLbl})` : ''}.
                    </p>
                    {versandart === 'palette' ? (
                        <p>
                            Der Lieferschein wird festgeschrieben. <b>Palette läuft nicht automatisch</b> — danach
                            die Excel im Schneider-Paletten-Portal hochladen.
                        </p>
                    ) : (
                        <p>
                            Der Lieferschein wird festgeschrieben und für <b>Schneiders API-Abholung</b> freigegeben
                            (SNV pollt ~alle 10 Min.). <b>Danach ist kein Storno mehr möglich.</b>
                        </p>
                    )}
                </>
            ),
            onConfirm: async () => {
                setSnvBusy(true);
                setSnvStatus({tone: 'work', text: `Lieferschein ${snvShip.number} wird versendet…`});
                try {
                    const j = await onDeliver.shipShipment(snvShip.id);
                    if (j.dryRun) {
                        setSnvStatus({tone: 'warn', text: `Trockenlauf: ${j.message || 'Es wurde nichts versendet.'}`});
                        toast(`Trockenlauf: ${j.message || 'Der Lieferschein wurde nicht versendet.'}`);
                        return;
                    }
                    if (j.ok) {
                        setSnvStatus({tone: 'ok', text: `✓ ${j.number || 'Lieferschein'} versendet`});
                        toast(`✓ ${j.number || 'Lieferschein'} versendet — Freigabe an Schneider erteilt`);
                        dispatch({type: 'wiz-patch', patch: {snvInfo: {id: null, data: null, loading: false}}});
                        onReload();
                        return;
                    }
                    setSnvStatus({tone: 'err', text: 'Versenden fehlgeschlagen: ' + (j.error || 'unbekannt')});
                } catch (e) {
                    setSnvStatus({tone: 'err', text: 'Versenden fehlgeschlagen: ' + (e.response?.data?.error || e.message)});
                } finally {
                    setSnvBusy(false);
                }
            },
        });

        const stornieren = () => ask({
            title: 'Schneider-Lieferschein stornieren',
            confirmLabel: 'Ja, stornieren',
            tone: 'danger',
            body: (
                <>
                    <p className="weg">
                        ⚠ Schneider-Lieferschein <b>{snvShip.number}</b> wird storniert.
                    </p>
                    <p>
                        Die Schneider-Positionen werden am Auftrag wieder offen — du kannst dann neu erzeugen. Geht
                        nur, <b>solange nicht versendet</b>.
                    </p>
                </>
            ),
            onConfirm: async () => {
                setSnvBusy(true);
                setSnvStatus({tone: 'work', text: `Lieferschein ${snvShip.number} wird storniert…`});
                try {
                    const j = await onDeliver.cancelShipment(snvShip.id);
                    if (j.dryRun) {
                        setSnvStatus({tone: 'warn', text: `Trockenlauf: ${j.message || 'Es wurde nichts storniert.'}`});
                        toast(`Trockenlauf: ${j.message || 'Der Lieferschein wurde nicht storniert.'}`);
                        return;
                    }
                    if (j.ok) {
                        setSnvStatus({tone: 'ok', text: `✓ ${j.number || 'Lieferschein'} storniert`});
                        toast(`✓ ${j.number || 'Lieferschein'} storniert — Positionen wieder offen${shortfallNote(j)}`);
                        dispatch({
                            type: 'wiz-patch',
                            patch: {
                                shipDocs: {id: null, docs: null, loading: false},
                                snvInfo: {id: null, data: null, loading: false},
                            },
                        });
                        dispatch({type: 'wiz-komm', patch: {schneiderDone: false}});
                        onReload();
                        return;
                    }
                    setSnvStatus({tone: 'err', text: 'Storno fehlgeschlagen: ' + (j.error || 'unbekannt')});
                } catch (e) {
                    setSnvStatus({tone: 'err', text: 'Storno fehlgeschlagen: ' + (e.response?.data?.error || e.message)});
                } finally {
                    setSnvBusy(false);
                }
            },
        });

        // Früh angelegter Lieferschein (automatisches Reservieren, 1.2.15): Er
        // steht im Status „Neu" und hält die Schneider-Ware für diesen Auftrag
        // zurück. Hier lassen sich die Versandart prüfen und der Beleg
        // festschreiben (erst dabei entsteht das PDF).
        const frueh = snvShip.status === 'NEW' && !shipped;

        const versandSetzen = async (kind) => {
            setSnvBusy(true);
            setSnvStatus({tone: 'work', text: 'Versandart wird eingetragen …'});
            try {
                const j = await setSnvVersand(orderRef, snvShip.id, kind);
                if (j.dryRun) {
                    setSnvStatus({tone: 'warn', text: 'Trockenlauf: Es wurde nichts eingetragen.'});
                } else if (j.ok) {
                    setSnvStatus({tone: 'ok', text: `✓ „${j.label || kind}" am Lieferschein eingetragen`});
                    dispatch({type: 'wiz-patch', patch: {snvInfo: {id: null, data: null, loading: false}}});
                } else {
                    setSnvStatus({tone: 'err', text: 'Eintragen fehlgeschlagen: ' + (j.error || 'unbekannt')});
                }
            } catch (e) {
                setSnvStatus({tone: 'err', text: 'Eintragen fehlgeschlagen: ' + (e.response?.data?.error || e.message)});
            } finally {
                setSnvBusy(false);
            }
        };

        const festschreiben = async () => {
            setSnvBusy(true);
            setSnvStatus({tone: 'work', text: `Lieferschein ${snvShip.number} wird festgeschrieben … (weclapp rendert den Beleg)`});
            try {
                const j = await finalizeShipment(orderRef, snvShip.id);
                if (j.dryRun) {
                    setSnvStatus({tone: 'warn', text: `Trockenlauf: ${j.message || 'Es wurde nichts festgeschrieben.'}`});
                } else if (j.ok) {
                    setSnvStatus({tone: 'ok', text: `✓ ${snvShip.number} festgeschrieben — das Beleg-PDF entsteht in weclapp`});
                    dispatch({
                        type: 'wiz-patch',
                        patch: {
                            shipDocs: {id: null, docs: null, loading: false},
                            snvInfo: {id: null, data: null, loading: false},
                        },
                    });
                    onReload();
                } else {
                    setSnvStatus({tone: 'err', text: 'Festschreiben fehlgeschlagen: ' + (j.error || 'unbekannt')});
                }
            } catch (e) {
                setSnvStatus({tone: 'err', text: 'Festschreiben fehlgeschlagen: ' + (e.response?.data?.error || e.message)});
            } finally {
                setSnvBusy(false);
            }
        };

        const imoSpeichern = async () => {
            const n = Number(imo);
            if (!Number.isFinite(n) || n < 0 || n > 9999) {
                setImoStatus('Bitte 0–9999 eingeben.');
                return;
            }
            setImoBusy(true);
            setImoStatus('speichere …');
            try {
                const j = await saveImoPallets(orderRef, snvShip.id, n);
                if (j.dryRun) {
                    setImoStatus(`Trockenlauf: ${j.message || 'nicht gespeichert'}`);
                } else if (j.ok) {
                    setImoStatus('✓ gespeichert');
                    dispatch({type: 'wiz-patch', patch: {snvInfo: {id: null, data: null, loading: false}}});
                } else {
                    setImoStatus('✕ ' + (j.error || 'Fehler'));
                }
            } catch (e) {
                setImoStatus('✕ ' + (e.response?.data?.error || e.message));
            } finally {
                setImoBusy(false);
            }
        };

        return (
            <>
                <UncoveredWarn anzahl={uc.length} onStep={onStep}/>

                <div className="srcnote">
                    <div className="srcq">
                        🚚 <b>Schneider-Lieferschein {snvShip.number}</b>
                        {shipped ? <span style={{color: 'var(--acc)'}}>✓ versendet</span> : <span>liegt vor</span>}
                        <span className="muted">
                            · ab {snvShip.warehouseName || 'SNV Weilheim'}
                            {vLbl ? ` · ${vLbl}` : ''}
                            {shipped ? ' · Freigabe an Schneider erteilt' : ' · noch nicht versendet'}
                        </span>
                    </div>
                </div>

                {frueh ? (
                    <div className="srcnote">
                        <div className="srcq">
                            🔒 <b>Reserviert, noch nicht festgeschrieben</b>
                            <span className="muted">
                                Der Editor hat diesen Lieferschein gleich nach der Mengenübernahme angelegt, damit
                                weclapp die Schneider-Ware für diesen Auftrag zurückhält. Er bleibt im Status „Neu" —
                                n8n reagiert erst beim Festschreiben und Versenden. Mengenänderungen zieht der Editor
                                beim Speichern nach.
                            </span>
                        </div>
                        <div className="srctoggle" style={{marginTop: 6}}>
                            {['paket', 'palette'].map((k) => (
                                <OeTip
                                    key={k}
                                    tip={k === 'paket'
                                        ? 'Trägt „Paket ab SNV" am Lieferschein ein. Schneider zieht den Beleg dann automatisch per API.'
                                        : 'Trägt „Palette ab SNV" am Lieferschein ein. Paletten laufen nicht automatisch — die Excel muss danach ins Schneider-Paletten-Portal.'}
                                >
                                    <button
                                        type="button"
                                        className={`srcopt${versandart === k ? ' on' : ''}`}
                                        aria-pressed={versandart === k}
                                        disabled={snvBusy || !canShip}
                                        onClick={() => versandSetzen(k)}
                                    >
                                        {k === 'paket' ? 'Paket ab SNV' : 'Palette ab SNV'}
                                    </button>
                                </OeTip>
                            ))}
                        </div>
                        <div style={{marginTop: 6}}>
                            <OeTip
                                tip={canShip
                                    ? 'Schreibt den Lieferschein fest: Erst dabei erzeugt weclapp das Beleg-PDF zum Drucken. Versendet wird er damit noch nicht.'
                                    : 'Zum Festschreiben fehlt das Recht „Versand".'}
                                detail={canShip
                                    ? 'Beim Festschreiben stößt weclapp außerdem die n8n-Abläufe an (Sammel-PDF, Zollinfo). Im Testmodus läuft das als Trockenlauf.'
                                    : undefined}
                            >
                                <button
                                    type="button"
                                    className="actbtn"
                                    disabled={snvBusy || !canShip}
                                    onClick={festschreiben}
                                >
                                    📄 Festschreiben (Beleg erzeugen)
                                </button>
                            </OeTip>
                        </div>
                    </div>
                ) : null}

                {doc ? (
                    <DocPanel
                        doc={doc}
                        icon="📄"
                        label="Schneider-Lieferschein"
                        orderRef={orderRef}
                        source={{type: 'shipment', id: snvShip.id}}
                        kommState={wizard.kommState}
                        canPrint={!!(abilities && abilities.warehouse)}
                        onPreferred={(name) => dispatch({
                            type: 'wiz-patch',
                            patch: {kommState: {...(wizard.kommState || {}), preferred: name}},
                        })}
                        hint={<>💡 Geht an Schneider. Den Lahr-Lieferschein erzeugst du erst in Schritt 9.</>}
                    />
                ) : (
                    <div className="lieferinfo">
                        {wizard.shipDocs.loading ? (
                            <span className="muted">Beleg wird gesucht …</span>
                        ) : (
                            <>
                                <span className="muted">
                                    Noch kein PDF am Lieferschein — weclapp legt es erst an, wenn der Lieferschein
                                    dort <b>festgeschrieben (gedruckt)</b> wird.
                                </span>
                                <OeTip tip="Sucht die Belege am Lieferschein erneut. Es wird nichts erzeugt oder geändert.">
                                    <button
                                        type="button"
                                        className="actbtn"
                                        onClick={() => dispatch({
                                            type: 'wiz-patch',
                                            patch: {
                                                shipDocs: {id: null, docs: null, loading: false},
                                                snvInfo: {id: null, data: null, loading: false},
                                            },
                                        })}
                                    >
                                        ↻ neu suchen
                                    </button>
                                </OeTip>
                                {order.webUrl ? (
                                    <OeTip tip="Öffnet den Auftrag in weclapp in einem neuen Browser-Tab.">
                                        <a className="wl" href={order.webUrl} target="_blank" rel="noopener noreferrer">
                                            Auftrag in weclapp öffnen ↗
                                        </a>
                                    </OeTip>
                                ) : null}
                            </>
                        )}
                    </div>
                )}

                <div className="srcnote">
                    <div className="srcq">
                        📦 <b>IMO Paletten</b>
                        <span className="muted">— Palettenanzahl für die Gefahrgut-Erklärung (IMO)</span>
                    </div>
                    <div style={{display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flex: '1 1 100%'}}>
                        <OeTip tip={'Anzahl der Paletten, die auf dem IMO-Beleg als „Pallets" angedruckt wird. 0 bis 9999.'}>
                            <input
                                className="rngi"
                                style={{width: 90}}
                                value={imo}
                                inputMode="numeric"
                                autoComplete="off"
                                placeholder="Anzahl"
                                aria-label="IMO Palettenanzahl"
                                onChange={(e) => setImo(e.target.value)}
                            />
                        </OeTip>
                        <OeTip tip="Schreibt die Palettenanzahl an den Lieferschein in weclapp. Umkehrbar — ein neuer Wert überschreibt den alten.">
                            <button type="button" className="actbtn" disabled={imoBusy || !canShip} onClick={imoSpeichern}>
                                Speichern
                            </button>
                        </OeTip>
                        <span className="muted">{imoStatus}</span>
                    </div>
                    <div className="lg-desc" style={{flex: '1 1 100%'}}>
                        Wird auf dem IMO-Beleg als „Pallets" angedruckt. Die Boxen (4G) schätzt der IMO-Workflow
                        automatisch (Dosen ÷ 6).
                    </div>
                </div>

                {!shipped ? (
                    <div className="liefergo staged">
                        <div className="stagestep">
                            <div className="stagehead">
                                <b>Versenden</b> — Freigabe an Schneider{vLbl ? ` (${vLbl})` : ''}
                            </div>
                            <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                                <OeTip tip={canShip
                                    ? 'Schreibt den Lieferschein fest und gibt ihn für Schneider frei. Danach ist kein Storno mehr möglich. Im Testmodus läuft das als Trockenlauf.'
                                    : 'Zum Versenden fehlt das Recht „Versand".'}>
                                    <button
                                        type="button"
                                        className="actbtn big"
                                        disabled={snvBusy || !canShip}
                                        onClick={versenden}
                                    >
                                        🚚 Versenden
                                    </button>
                                </OeTip>
                                <OeTip tip={canShip
                                    ? 'Storniert den Lieferschein; die Schneider-Positionen werden am Auftrag wieder offen. Geht nur, solange nicht versendet. Im Testmodus läuft das als Trockenlauf.'
                                    : 'Zum Stornieren fehlt das Recht „Versand".'}>
                                    <button
                                        type="button"
                                        className="actbtn"
                                        style={{borderColor: 'var(--warn)', color: 'var(--warn)'}}
                                        disabled={snvBusy || !canShip}
                                        onClick={stornieren}
                                    >
                                        ✕ Lieferschein stornieren
                                    </button>
                                </OeTip>
                            </div>
                            <div className="lg-desc">
                                „Versenden" schreibt den Lieferschein fest (versendet) — ab dann zieht{' '}
                                <b>Schneider den Beleg per API</b> (pollt ~alle 10 Min.) und meldet seinen Status
                                zurück. <b>Storno nur, solange NICHT versendet.</b>
                            </div>
                        </div>
                        {snvStatus ? <div className={`status ${snvStatus.tone}`}>{snvStatus.text}</div> : null}
                    </div>
                ) : versandart === 'palette' ? (
                    <div className="liefergo staged">
                        <div className="stagestep done">
                            <div className="stagehead">✓ <b>Versendet</b> — als Palette gekennzeichnet</div>
                            <div className="lg-desc">
                                📋 <b>Palette läuft NICHT automatisch.</b> Bitte die Excel aus dem Auftrag
                                herunterladen und im <b>Schneider-Paletten-Portal</b> hochladen. (Nur bei „Paket"
                                zieht Schneider automatisch per API.)
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="liefergo staged">
                        <div className="stagestep done">
                            <div className="stagehead">
                                ✓ <b>Versendet</b> — Freigabe an Schneider erteilt
                                {versandart === 'paket' ? ' (Paket, API-Abholung)' : ''}
                            </div>
                            <div className="lg-desc">
                                Schneider Versandstatus:{' '}
                                {wizard.snvInfo.loading && !info
                                    ? <span className="muted">wird geladen …</span>
                                    : (info && info.schneiderStatus
                                        ? <b>{info.schneiderStatus}</b>
                                        : <span className="muted">— noch keine Rückmeldung</span>)}
                                {' '}
                                <OeTip tip="Holt den Schneider-Versandstatus erneut aus weclapp. Es wird nichts geändert.">
                                    <button
                                        type="button"
                                        className="actbtn"
                                        onClick={() => dispatch({
                                            type: 'wiz-patch',
                                            patch: {snvInfo: {id: null, data: null, loading: false}},
                                        })}
                                    >
                                        ↻ aktualisieren
                                    </button>
                                </OeTip>
                                <br/>
                                SNV pollt ~alle 10 Min. Steht hier <b>error</b>, bitte in weclapp prüfen;{' '}
                                <b>processing/success</b> = läuft.
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    // ── Fall „noch kein Lieferschein" ───────────────────────────────────────
    const sv = snvVersandInfo(plan.secondaryRows);
    const svOpt = (k, titel, sub, tip) => (
        <OeTip tip={tip}>
            <button
                type="button"
                className={`srcopt${wizard.snvVersand === k ? ' on' : ''}`}
                aria-pressed={wizard.snvVersand === k}
                onClick={() => dispatch({type: 'wiz-patch', patch: {snvVersand: k}})}
            >
                {titel}
                <span>{sub}{sv.kind === k ? <> · <b>Vorschlag</b></> : null}</span>
            </button>
        </OeTip>
    );

    return (
        <>
            <UncoveredWarn anzahl={uc.length} onStep={onStep}/>

            <SourceNote
                norm={norm}
                laName={laName}
                snName={snName}
                orderSource={wizard.orderSource}
                onSource={(v) => dispatch({type: 'wiz-patch', patch: {orderSource: v}})}
            />

            <div className="srcnote">
                <div className="srcq">
                    📦 <b>Wie geht die Schneider-Sendung raus?</b>{' '}
                    <span>
                        {fmt0(sv.stk)} Stück ≈ <b>{sv.pakete} Paket(e)</b> (gerechnet mit {SNV_STK_JE_PAKET} Stück
                        je Paket).
                    </span>
                </div>
                <div className="srctoggle">
                    {svOpt('paket', 'Paket ab SNV', 'als einzelne Pakete',
                        'Trägt „Paket ab SNV" am Lieferschein ein. Schneider zieht den Beleg dann automatisch per API.')}
                    {svOpt('palette', 'Palette ab SNV', `ab ca. ${SNV_PALETTE_AB_STK} Stück`,
                        'Trägt „Palette ab SNV" am Lieferschein ein. Paletten laufen nicht automatisch — die Excel muss danach ins Schneider-Paletten-Portal.')}
                </div>
            </div>

            <VeVorschau ctx={ctx} rows={plan.secondaryRows}/>

            <div className="liefergo staged">
                <div className="stagestep">
                    <div className="stagehead">
                        <b>Schneider-Lieferschein</b> · {plan.secondaryRows.length} Position(en) ab {snName}
                    </div>
                    <OeTip
                        tip={canShip
                            ? 'Erzeugt in weclapp den Lieferschein ab Schneider (SNV) für die Schneider-Positionen und schreibt ihn fest.'
                            : 'Zum Erzeugen von Lieferscheinen fehlt das Recht „Versand".'}
                        detail={canShip
                            ? 'Trägt dabei auch die Versandart ein. Versendet wird er erst mit dem Knopf „Versenden" darunter. Im Testmodus läuft das als Trockenlauf.'
                            : undefined}
                    >
                        <button
                            type="button"
                            className="actbtn big"
                            disabled={deliver.running || openShip || !wizard.snvVersand || !canShip}
                            onClick={() => onDeliver.run('secondary')}
                        >
                            🚚 Schneider-Lieferschein erzeugen
                        </button>
                    </OeTip>
                    <div className="lg-desc">
                        {openShip
                            ? '🔒 Es gibt bereits einen offenen Lieferschein — bitte zuerst abschließen.'
                            : (wizard.snvVersand
                                ? <>
                                    Erzeugt den Lieferschein ab Schneider (SNV) für die Schneider-Positionen und trägt{' '}
                                    <b>{wizard.snvVersand === 'palette' ? 'Palette ab SNV' : 'Paket ab SNV'}</b> ein.
                                </>
                                : <>🔒 Erst oben <b>Paket oder Palette</b> wählen — weclapp verlangt die Angabe am
                                    Schneider-Lieferschein.</>)}
                    </div>
                </div>

                {deliver.status ? (
                    <div className={`status ${deliver.status.tone}`} style={{marginTop: 8}}>{deliver.status.text}</div>
                ) : null}

                <DeliverResult
                    result={deliver.result}
                    docs={deliver.docs}
                    orderRef={orderRef}
                    orderId={order.orderId}
                    kommState={wizard.kommState}
                    canPrint={!!(abilities && abilities.warehouse)}
                    onToast={toast}
                />
            </div>
        </>
    );
}
